import type { WorkflowNodeInput } from '@cotera/api';
import { Assert } from '@cotera/utilities';
import {
  cloneWorkflowDraftState,
  createDraftStateFromGraph,
  draftDeleteWorkflowNode,
  draftUpdateNodeData,
  type WorkflowDraftState,
} from './workflow-draft-graph';
import type { WorkflowGraphApiData } from './workflow-graph.types';

/**
 * The edits the nested-workflow tree offers.
 *
 * Deliberately only the *hierarchy*: which workflow a step runs, and whether the
 * step is there at all. Everything else about a node is edited where it already
 * has a home — the box's "open" button, one click from here, with the real
 * editor and its inspector.
 */
export type NestedHierarchyEdit =
  | {
      readonly kind: 'repoint';
      readonly nodeId: string;
      readonly targetWorkflowId: string;
      readonly targetWorkflowName: string;
    }
  | { readonly kind: 'remove'; readonly nodeId: string };

/** Context the draft helpers need to stamp new rows. */
export type NestedHierarchyEditContext = {
  readonly workflowId: string;
  readonly orgId: string;
  readonly userId: string;
};

/**
 * The node payload for a `run-workflow` step pointed at `targetWorkflowId`.
 *
 * Deliberately drops `inputBindings`. They are keyed to the *old* target's entry
 * fields, so carrying them across would leave the step wired to inputs the new
 * workflow does not have — the node editor clears them on the same event (see
 * `RunWorkflowNodeEditor`'s `onValueChange`).
 */
export function runWorkflowNodeInput(params: {
  targetWorkflowId: string;
  targetWorkflowName: string;
}): WorkflowNodeInput {
  const name = params.targetWorkflowName.trim();
  return {
    type: 'run-workflow',
    data: {
      targetWorkflowId: params.targetWorkflowId,
      ...(name !== '' ? { name } : {}),
    },
  };
}

/**
 * Applies one hierarchy edit to a graph, away from any editor model.
 *
 * This is the write path for the workflows in the tree that the page holds no
 * model for. It runs the *same* `draft*` helpers the editor's mutations do, so a
 * re-point made here and one made on the canvas cannot drift apart; only the
 * commit differs (a whole-graph update, rather than a draft the page autosaves).
 *
 * Returns both drafts because the graph serializer diffs against the baseline to
 * decide whether a trigger is bound or created.
 */
export function applyNestedHierarchyEdit(params: {
  graph: WorkflowGraphApiData;
  ctx: NestedHierarchyEditContext;
  edit: NestedHierarchyEdit;
  idempotencyKey?: WorkflowDraftState['idempotencyKey'];
}): { draft: WorkflowDraftState; baseline: WorkflowDraftState } {
  const { graph, ctx, edit } = params;
  const baseline = createDraftStateFromGraph(ctx, graph, {
    idempotencyKey: params.idempotencyKey ?? null,
  });
  const start = cloneWorkflowDraftState(baseline);

  switch (edit.kind) {
    case 'repoint':
      return {
        baseline,
        draft: draftUpdateNodeData(
          start,
          edit.nodeId,
          runWorkflowNodeInput(edit)
        ),
      };
    case 'remove':
      return { baseline, draft: draftDeleteWorkflowNode(start, edit.nodeId) };
    default:
      return Assert.unreachable(edit);
  }
}

/**
 * How many steps lose a connection when `nodeId` goes.
 *
 * Removing a step mid-chain leaves what came before it and what came after it
 * unlinked — the same consequence deleting it on the canvas has, which the canvas
 * spells out before doing it. The tree has to say so too.
 */
export function countNestedHierarchyRemovalImpact(
  graph: Pick<WorkflowGraphApiData, 'edges'>,
  nodeId: string
): { upstreamCount: number; downstreamCount: number } {
  const upstream = new Set<string>();
  const downstream = new Set<string>();
  for (const edge of graph.edges) {
    if (edge.toNodeId === nodeId) {
      upstream.add(edge.fromNodeId);
    }
    if (edge.fromNodeId === nodeId) {
      downstream.add(edge.toNodeId);
    }
  }
  return { upstreamCount: upstream.size, downstreamCount: downstream.size };
}
